/* eslint-disable react/jsx-no-literals */
// components/budget-panel/CapexOpexView.tsx
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import {
  BudgetSummary,
  FilterBudget,
  SectionBudget,
  TimePeriodBudget,
} from '@/features/canvas/types/budget-type';
import { formatCurrency } from '@/features/canvas/utils/currency';

import { BreakdownSelector } from './BreakdownSelector';

interface Props {
  breakdownView: string;
  currency: string;
  filterBreakdowns: Record<number, FilterBudget[]>;
  isLoading: boolean;
  onBreakdownChange: (value: string) => void;
  sectionBreakdown: SectionBudget[];
  summary: BudgetSummary | undefined;
  timePeriodBreakdown: TimePeriodBudget[];
}

export const CapexOpexView: React.FC<Props> = ({
  summary,
  timePeriodBreakdown,
  sectionBreakdown,
  filterBreakdowns,
  currency,
  isLoading,
  breakdownView,
  onBreakdownChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { filters } = useEditorContext();

  // Calculate total for percentage calculations
  const totalBudget = summary?.total_budget || 0;
  const totalCapex = summary?.total_capex || 0;
  const totalOpex = summary?.total_opex || 0;

  const capexPercentage = totalBudget > 0 ? Math.round((totalCapex / totalBudget) * 100) : 0;
  const opexPercentage = totalBudget > 0 ? Math.round((totalOpex / totalBudget) * 100) : 0;

  if (isLoading) {
    return <LoadingState>{t('loading')}</LoadingState>;
  }

  // Helper function to render filter breakdowns
  const renderFilterBreakdown = (filterId: number) => {
    const breakdowns = filterBreakdowns[filterId] || [];
    const filter = filters?.find(f => f.id === filterId);

    return breakdowns.map(filterOption => {
      const optionCapex = Number(filterOption.capex) || 0;
      const optionOpex = Number(filterOption.opex) || 0;
      const optionTotal = optionCapex + optionOpex;

      const optionCapexPercentage =
        optionTotal > 0 ? Math.round((optionCapex / optionTotal) * 100) : 0;
      const optionOpexPercentage =
        optionTotal > 0 ? Math.round((optionOpex / optionTotal) * 100) : 0;

      // Find the color for this option
      const optionColor =
        filter?.options?.find(opt => opt.id === filterOption.filter_option_id)?.color || '#6e6e72';

      return (
        <BreakdownItem key={filterOption.filter_option_id}>
          <ItemHeader>
            <ItemTitleWrapper>
              <ColorCircle $color={optionColor} />
              <ItemTitle>{filterOption.filter_option_title}</ItemTitle>
            </ItemTitleWrapper>
          </ItemHeader>
          <Divider />
          <CapexOpexBreakdown>
            <GridRow>
              <CapexOpexLabel>CapEx</CapexOpexLabel>
              <CapexOpexValue $color="#616161">
                {formatCurrency(optionCapex, currency)}
              </CapexOpexValue>
              <CapexOpexPercentage>{optionCapexPercentage}%</CapexOpexPercentage>
            </GridRow>
            <GridRow>
              <CapexOpexLabel>OpEx</CapexOpexLabel>
              <CapexOpexValue $color="#a5a5a5">
                {formatCurrency(optionOpex, currency)}
              </CapexOpexValue>
              <CapexOpexPercentage>{optionOpexPercentage}%</CapexOpexPercentage>
            </GridRow>
          </CapexOpexBreakdown>
        </BreakdownItem>
      );
    });
  };

  return (
    <Container>
      {/* Summary */}
      <Section>
        <ProgressBarsContainer>
          <ProgressBarRow>
            <ProgressBarLabelContainer>
              <ProgressBarLabel>CapEx</ProgressBarLabel>
            </ProgressBarLabelContainer>
            <ProgressBarWrapper>
              <ProgressBar>
                <ProgressBarInner>
                  <ProgressFill
                    $percentage={Math.min(100, capexPercentage)}
                    $color={theme.palette.brand.editorTabPrimary}
                  />
                </ProgressBarInner>
                <ProgressPercentage>{capexPercentage}%</ProgressPercentage>
              </ProgressBar>
              <ProgressBarValue $color={theme.palette.brand.editorTabPrimary}>
                {formatCurrency(totalCapex, currency)}
              </ProgressBarValue>
            </ProgressBarWrapper>
          </ProgressBarRow>
          <ProgressBarTotal>{formatCurrency(totalCapex, currency)}</ProgressBarTotal>

          <ProgressBarRow>
            <ProgressBarLabelContainer>
              <ProgressBarLabel>OpEx</ProgressBarLabel>
            </ProgressBarLabelContainer>
            <ProgressBarWrapper>
              <ProgressBar>
                <ProgressBarInner>
                  <ProgressFill $percentage={Math.min(100, opexPercentage)} $color="#a5a5a5" />
                </ProgressBarInner>
                <ProgressPercentage>{opexPercentage}%</ProgressPercentage>
              </ProgressBar>
              <ProgressBarValue $color="#a5a5a5">
                {formatCurrency(totalOpex, currency)}
              </ProgressBarValue>
            </ProgressBarWrapper>
          </ProgressBarRow>
          <ProgressBarTotal>{formatCurrency(totalOpex, currency)}</ProgressBarTotal>
        </ProgressBarsContainer>
      </Section>

      {/* Breakdown with Selector */}
      <Section>
        <BreakdownHeader>
          <DisplayByLabel>Display by:</DisplayByLabel>
          <BreakdownSelector value={breakdownView} onChange={onBreakdownChange} />
        </BreakdownHeader>

        <BreakdownList>
          {breakdownView === 'timePeriod' &&
            timePeriodBreakdown?.map(period => {
              // Ensure values are numbers
              const periodCapex = Number(period.capex) || 0;
              const periodOpex = Number(period.opex) || 0;
              const periodTotal = periodCapex + periodOpex;

              const periodCapexPercentage =
                periodTotal > 0 ? Math.round((periodCapex / periodTotal) * 100) : 0;
              const periodOpexPercentage =
                periodTotal > 0 ? Math.round((periodOpex / periodTotal) * 100) : 0;

              return (
                <BreakdownItem key={period.time_period_id}>
                  <ItemHeader>
                    <ItemTitle>{period.time_period}</ItemTitle>
                  </ItemHeader>
                  <Divider />
                  <CapexOpexBreakdown>
                    <GridRow>
                      <CapexOpexLabel>CapEx</CapexOpexLabel>
                      <CapexOpexValue $color="#616161">
                        {formatCurrency(periodCapex, currency)}
                      </CapexOpexValue>
                      <CapexOpexPercentage>{periodCapexPercentage}%</CapexOpexPercentage>
                    </GridRow>
                    <GridRow>
                      <CapexOpexLabel>OpEx</CapexOpexLabel>
                      <CapexOpexValue $color="#a5a5a5">
                        {formatCurrency(periodOpex, currency)}
                      </CapexOpexValue>
                      <CapexOpexPercentage>{periodOpexPercentage}%</CapexOpexPercentage>
                    </GridRow>
                  </CapexOpexBreakdown>
                </BreakdownItem>
              );
            })}

          {breakdownView === 'section' &&
            sectionBreakdown?.map(section => {
              // Ensure values are numbers
              const sectionCapex = Number(section.capex) || 0;
              const sectionOpex = Number(section.opex) || 0;
              const sectionTotal = sectionCapex + sectionOpex;

              const sectionCapexPercentage =
                sectionTotal > 0 ? Math.round((sectionCapex / sectionTotal) * 100) : 0;
              const sectionOpexPercentage =
                sectionTotal > 0 ? Math.round((sectionOpex / sectionTotal) * 100) : 0;

              return (
                <BreakdownItem key={section.section_id}>
                  <ItemHeader>
                    <ItemTitle>{section.section}</ItemTitle>
                  </ItemHeader>
                  <Divider />
                  <CapexOpexBreakdown>
                    <GridRow>
                      <CapexOpexLabel>CapEx</CapexOpexLabel>
                      <CapexOpexValue $color="#616161">
                        {formatCurrency(sectionCapex, currency)}
                      </CapexOpexValue>
                      <CapexOpexPercentage>{sectionCapexPercentage}%</CapexOpexPercentage>
                    </GridRow>
                    <GridRow>
                      <CapexOpexLabel>OpEx</CapexOpexLabel>
                      <CapexOpexValue $color="#a5a5a5">
                        {formatCurrency(sectionOpex, currency)}
                      </CapexOpexValue>
                      <CapexOpexPercentage>{sectionOpexPercentage}%</CapexOpexPercentage>
                    </GridRow>
                  </CapexOpexBreakdown>
                </BreakdownItem>
              );
            })}

          {breakdownView.startsWith('filter-') &&
            renderFilterBreakdown(parseInt(breakdownView.replace('filter-', '')))}
        </BreakdownList>
      </Section>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BreakdownHeader = styled.div`
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const DisplayByLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #363649;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
`;

const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0.5rem;
  padding: 1.5rem;
`;

const ProgressBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ProgressBarLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 50px;
`;

const ProgressBarLabel = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabSecondary};
`;

const ProgressBarTotal = styled.span`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};

  &:first-of-type {
    margin-bottom: 1.5rem;
  }
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const ProgressBar = styled.div`
  height: 20px;
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  flex: 1;
  min-width: 150px;
  padding: 3px;
  position: relative;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
`;

const ProgressFill = styled.div<{ $color: string; $percentage: number }>`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background: ${({ $color }) => $color};
  transition: width 0.3s ease;
  border-radius: ${({ $percentage }) => ($percentage >= 100 ? '6px' : '0 6px 6px 0')};
`;

const ProgressPercentage = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.5rem;
  color: #707070;
  z-index: 1;
`;

const ProgressBarValue = styled.span<{ $color: string }>`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ $color }) => $color};
  min-width: 100px;
  text-align: right;
  white-space: nowrap;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BreakdownItem = styled.div`
  padding: 1rem;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0.5rem;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ItemTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ColorCircle = styled.div<{ $color: string }>`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ItemTitle = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #9a9aa3;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #26a5a2;
  opacity: 32%;
  margin-bottom: 1.25rem;
`;

const CapexOpexBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 50px;
  gap: 0.5rem;
  align-items: center;
`;

const CapexOpexLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabSecondary};
`;

const CapexOpexValue = styled.span<{ $color?: string }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ $color, theme }) => $color || theme.palette.text.primary};
  text-align: right;
`;

const CapexOpexPercentage = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #363649;
  text-align: right;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.palette.text.secondary};
`;
